import React from "react";
import { useRootAPI } from "@/api";
import { SvgIcon } from "@/svg";
import { useBudgetContext } from "./useBudgetContext";
import * as Sentry from "@sentry/react";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";

const TEMPLATE_FILES_OPTIONS = [
  { id: GetBudgetResolutionEnum.monthly, label: "Monthly", fileName: "monthly-budget" },
  { id: GetBudgetResolutionEnum.weekly, label: "Weekly", fileName: "weekly-budget" },
] as const;

const getResolutionLabel = (resolution: GetBudgetResolutionEnum): string => {
  return TEMPLATE_FILES_OPTIONS.find((option) => option.id === resolution)?.label ?? resolution;
};

const getResolutionFileName = (resolution: GetBudgetResolutionEnum): string => {
  return TEMPLATE_FILES_OPTIONS.find((option) => option.id === resolution)?.fileName ?? resolution;
};

type TemplateDownloadProps = {
  resolution: GetBudgetResolutionEnum;
  className?: string;
  year: number;
};

function TemplateDownloadButton({ resolution, className }: TemplateDownloadProps) {
  const rootAPI = useRootAPI();
  const { selectedYear, selectedBusinessId } = useBudgetContext();

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!selectedBusinessId) {
      console.error("No business selected");
      Sentry.captureException(
        new Error("Attempted to download budget template without business selected"),
        {
          tags: {
            component: "TemplateDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
          },
        },
      );
      return;
    }

    const requestParams = {
      resolution,
      year: selectedYear,
      business_id: selectedBusinessId,
    };

    let url: string | undefined;
    let link: HTMLAnchorElement | undefined;

    try {
      const blob = await rootAPI.getBudgetTemplate(requestParams);

      url = window.URL.createObjectURL(blob);
      link = document.createElement("a");
      link.href = url;
      link.download = `${getResolutionFileName(resolution)}-template-${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: {
            component: "TemplateDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
            requestDetails: requestParams,
          },
        });
        console.error("Error during template download:", error);
      }
    } finally {
      if (link) {
        document.body.removeChild(link);
      }
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center cursor-pointer" onClick={handleDownload}>
        <SvgIcon icon="Download" />
        <a
          href="#"
          className="text-primaryColor hover:underline ml-2"
          onClick={(e) => e.preventDefault()}
        >
          {getResolutionLabel(resolution)}
        </a>
      </div>
    </div>
  );
}

export function TemplateDownloadSection() {
  const { selectedYear } = useBudgetContext();

  return (
    <div className="flex items-center gap-8">
      <span className="text-dark">Templates:</span>
      <div className="flex items-center gap-7">
        <TemplateDownloadButton resolution={GetBudgetResolutionEnum.weekly} year={selectedYear} />
        <TemplateDownloadButton resolution={GetBudgetResolutionEnum.monthly} year={selectedYear} />
      </div>
    </div>
  );
}
